
import { ArvGalleryIcon } from '../../assets/arv/ArvGalleryIcon'
import { ExcludeIcon } from '../../assets/arv/ExcludeIcon'
import { RestoreIcon } from '../../assets/RestoreIcon'
import { compDisplayPrice } from './compPrice'
import { compAge, compDate } from './compAge'
import { parseCalendarDate } from './calendarDate'
function formatPrice(n) {
  if (!n) return ''
  return '$' + Number(n).toLocaleString()
}

// Used only by getVerifyColumns below, which is the internal verification grid and not the comp
// table the spec describes.
function formatDate(dateStr) {
  if (!dateStr) return ''
  return parseCalendarDate(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

// ─── Style tokens ─────────────────────────────────────────────────────────────

const headerCls = { fontWeight: 700, fontSize: 14, color: '#17270D' }

// Score bands, from files/sow2/spec/arv.md section 6 (frames comp_scores/investment-score_1..3).
// Both the shades and the middle boundary moved: the code shipped #43B017 / #7EBA56 / #17270D with
// `> 59`, which put a score of exactly 60 in the middle band where the design puts it in the bottom
// one.
//
// No gap between 60 and 61. calcScore (reilm-be utils/arvFetch.js) sums nine integer constants and
// clamps with Math.max/Math.min, so a comp score is always a whole number and 60.5 cannot exist. The
// bands are written as >= 70 and >= 61 rather than > 60.99 for that reason, and this is where it is
// recorded, since the spec bands read "0-60" and "61-69" and invite the question.
const SCORE_BANDS = [
  { min: 70, color: '#43A824' },
  { min: 61, color: '#5F6F59' },
  { min: 0,  color: '#8C9489' },
]
const scoreColor = (value) => (SCORE_BANDS.find((b) => value >= b.min) ?? SCORE_BANDS[2]).color

// Status tag: 18x18, radius 4, one white letter at 12px, plus the full word on hover at 10px.
const STATUS_TAGS = {
  Sold:    { letter: 'S', bg: '#43B017' },
  Active:  { letter: 'A', bg: '#C13A4A' },
  Pending: { letter: 'P', bg: '#E8B611' },
}

// The tooltip is drawn inside the cell, below the tag, and deliberately small. A DataGrid clips at
// the virtual scroller, so anything escaping the row is cut off — with a 72px row and an 18px tag
// there is room underneath, which is why this does not need a portal.
function StatusTag({ value }) {
  const tag = STATUS_TAGS[value]
  if (!tag) return null
  return (
    <div className="relative group flex items-center justify-center">
      <span
        className="flex h-[18px] w-[18px] items-center justify-center rounded-[4px]"
        style={{ background: tag.bg }}
      >
        <span className="text-[12px] leading-none text-white">{tag.letter}</span>
      </span>
      <span
        className="absolute top-full left-1/2 -translate-x-1/2 mt-[4px] hidden group-hover:block
                   whitespace-nowrap rounded-[4px] bg-white px-[6px] py-[2px] text-[10px]
                   text-brand-dark shadow-md pointer-events-none z-10"
      >
        {value}
      </span>
    </div>
  )
}

// ─── Icons ───────────────────────────────────────────────────────────────────


// ─── Column definitions ───────────────────────────────────────────────────────
//
// ARV Comp Grid. Every value below is from the spec table in files/sow2/spec/arv.md section 6, which
// was extracted from Figma on 18.08 with the frame name beside each row. The block that used to
// stand here described a different set of sizes and colours and had drifted from both the design and
// the code, which is why the values now live one place and are cited rather than copied.
//
// Header row:  bg #E7F0E2 · border-bottom #DAE4D5 · h-[52px] · 14px bold #17270D
// Data rows:   h-[72px] · hover bg #F7F9F6 (Comment Set #1, ARV 7.4; was #E8F5E1)
//
// Score       70px  centered  bold 14px, colour by band (SCORE_BANDS above)
// Property   320px  left      line 1 street 14px #17270D · line 2 city/state/zip + specs 13px #4F5F4A
// Date        80px  left      value 14px #17270D + unit 13px #4F5F4A, one line
// Distance   100px  centered  14px #17270D
// Status      70px  centered  18x18 r4 tag, 12px white letter, full word on hover at 10px
// Price      150px  right     14px #17270D · sub 13px #4F5F4A `Adjusted: $x`, em dash #8A9A85 when none
// Actions     90px  centered  gallery · X (exclude) / Restore (put back)

export function getArvColumns({ onRowClick, onToggleArchived, onGalleryOpen }) {
  return [
    // ── Score ────────────────────────────────────────────────────────────────
    {
      field: 'score',
      headerName: 'Score',
      width: 70,
      sortable: false,
      disableColumnMenu: true,
      headerAlign: 'center',
      align: 'center',
      renderHeader: () => <span style={headerCls}>Score</span>,
      // Comment Set #1, ARV 7.6: about 5% letter spacing on the Score values. The lean export
      // draws this node at ls=0em, so the spacing is his instruction rather than a value we
      // missed; it stays until he says otherwise.
      renderCell: ({ value }) => (
        <span className="font-bold text-[14px]" style={{ color: scoreColor(value ?? 0), letterSpacing: '0.05em' }}>
          {value ?? ''}
        </span>
      ),
    },

    // ── Property ─────────────────────────────────────────────────────────────
    //
    // The header reads Property, and the two lines split where the design splits them: the street on
    // its own, then city/state/zip together with beds, baths, sqft and year built. City used to sit
    // on line one beside the street; the spec row for line two is literal —
    // `Denver, CO 80202 3 bd • 2 ba • 1,850 sqft • 1974 yb`.
    {
      field: 'address',
      headerName: 'Property',
      width: 320,
      sortable: false,
      disableColumnMenu: true,
      renderHeader: () => <span style={headerCls}>Property</span>,
      renderCell: ({ row }) => {
        const { street, city, state, zip } = row.address ?? {}
        const citystatezip = [city, [state, zip].filter(Boolean).join(' ')].filter(Boolean).join(', ')
        // Non-blank rather than truthy: a whitespace-only street is reachable by a direct POST
        // (`createPropertyService` tests `!body?.address?.street`) and would otherwise draw both its
        // own run and the separator, indenting the line by a measured 6.81px.
        const hasStreet = Boolean(street && String(street).trim())
        const addressDisplay = [street, citystatezip].filter((part) => part && String(part).trim()).join(' ')
        const { beds, baths, sqft } = row.specs ?? {}
        const specs = [
          beds   ? `${beds} bd`                            : null,
          baths  ? `${baths} ba`                           : null,
          sqft   ? `${Number(sqft).toLocaleString()} sqft`  : null,
          row.year_built ? `${row.year_built} yb`           : null,
        ].filter(Boolean).join(' • ')
        // Comment Set #1, ARV 7.7: city, state and zip move up to row 1, so row 2 is the spec run
        // alone — which is what the export draws (`sheet-row-default/Frame 176_2`, y 1340.4).
        const sub = specs
        return (
          <div
            className="flex flex-col justify-center gap-y-[2px] cursor-pointer py-[16px] w-[100%] group"
            onClick={() => onRowClick(row)}
            title={addressDisplay}
          >
            {/* Comment Set #1, ARV 7.1: "Update Property column and values similarly to Pipeline",
                and Pipeline 1 is "Bold the address under Property column". The export draws this
                node 600 and the Pipeline one bold; his sentence asks for parity with Pipeline, so
                bold is the reading that satisfies it.

                7.7 puts city, state and zip on this same line — done now, and it is the same one
                line the Pipeline row draws. SOW 4 A1 (canvas: "apply to Pipeline/ARV"): city/state/zip
                14/400 #17270D, line 2 13/400 #17270D, line 1 hover #328C12. Line 2's hover is A4's. */}
            {/* `truncate` here and the `title` on the wrapper are what the Pipeline copy of this
                cell has carried since mr-05, and this one did not until 03.09. ARV 7.7 is what made
                it matter: with city, state and zip moved onto this line, the line got long enough
                to leave the column. Measured on the live grid at 1280 with a 44-character street,
                the city run's right edge landed **107.59px past the cell's own right edge** —
                `.MuiDataGrid-cell` is `overflow: hidden`, so it was cut mid-glyph, and the cell's
                own `text-overflow: ellipsis` cannot reach text nested two boxes down, so there was
                no ellipsis and no tooltip either. It read as a complete address missing its zip.
                The margin is thin rather than theoretical: across the 25 longest comp addresses
                recorded in this repo the widest lands at 318.2 against a 320 column.
                The hover colour is on this outer span as well as on both runs, because on a
                truncated row the ellipsis is generated by THIS box. */}
            <span className="text-[14px] text-brand-dark leading-tight truncate group-hover:text-brand-hover">
              {hasStreet ? <span className="font-bold group-hover:text-brand-hover">{street}</span> : null}
              {citystatezip ? (
                <>
                  {/* Conditional on BOTH runs, and the street half is tested for non-blank rather
                      than for truthy — `' '` is truthy.
                      What the empty-string case fixes is narrower than this comment first claimed:
                      CSS collapses a leading space at the start of a line, so the city run starts at
                      x 0 with the separator present and absent alike, measured under both
                      `white-space` modes. The divergence it closes is between the DOM text and the
                      `title` composed beside it, and that is what the tests assert. The one state
                      that does paint a visible indent is the whitespace street above. */}
                  {hasStreet ? ' ' : null}
                  <span className="text-[14px] font-normal text-brand-dark group-hover:text-brand-hover">
                    {citystatezip}
                  </span>
                </>
              ) : null}
            </span>
            {/* Comment Set #1, Pipeline 1.3, whose own sentence says "apply to Pipeline/ARV". Row 2
                is now the spec run on its own, which is the set that sentence names. */}
            <span className="text-[13px] font-normal text-brand-dark group-hover:text-brand-green leading-tight">{sub}</span>
          </div>
        )
      },
    },

    // ── Date ─────────────────────────────────────────────────────────────────
    {
      field: 'last_sold_date',
      headerName: 'Date',
      width: 80,
      sortable: false,
      disableColumnMenu: true,
      renderHeader: () => <span style={headerCls}>Date</span>,
      renderCell: ({ row }) => {
        const age = compAge(compDate(row))
        if (!age) return null
        return (
          <span className="flex items-baseline gap-x-[3px]">
            {/* ARV 7.2: the number is SemiBold, the unit is not. 14/600 and 13/regular on
                table/sheet-row-default_16/Frame 466 in sow2-section-lean.svg. */}
            <span className="text-[14px] font-semibold text-brand-dark leading-tight">{age.value}</span>
            <span className="text-[13px] text-brand-secondary leading-tight">{age.unit}</span>
          </span>
        )
      },
    },

    // ── Distance ─────────────────────────────────────────────────────────────
    {
      field: 'distanceSubj',
      headerName: 'Distance',
      width: 100,
      sortable: false,
      disableColumnMenu: true,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => <span style={headerCls}>Distance</span>,
      // ARV 7.2: 14/600 on table/sheet-row-default_16/Frame 189.
      renderCell: ({ value }) => (
        <span style={{ fontSize: 14, fontWeight: 600, color: '#17270D' }}>
          {value != null ? `${value.toFixed(2)}` : ''}
        </span>
      ),
    },

    // ── Status ───────────────────────────────────────────────────────────────
    {
      field: 'status',
      headerName: 'Status',
      width: 70,
      sortable: false,
      disableColumnMenu: true,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => <span style={headerCls}>Status</span>,
      renderCell: ({ value }) => <StatusTag value={value} />,
    },

    // ── Price ────────────────────────────────────────────────────────────────
    {
      field: 'price',
      headerName: 'Price',
      width: 150,
      sortable: false,
      disableColumnMenu: true,
      align: 'right',
      headerAlign: 'right',
      renderHeader: () => <span style={headerCls}>Price</span>,
      // The em dash says "no data" out loud, and it is here rather than inside formatPrice: that
      // helper also renders the price above, where an empty string is the right answer for a comp
      // whose price we do not have — the sub-line is the only one carrying a label that would
      // otherwise stand with nothing after it.
      //
      // This is what Микола saw and asked about on 26.08 ("Why did one of the comps not produce an
      // Adjusted Price?"): a Sold comp with no sale price from the provider got adjustments 0, and
      // `formatPrice` starts with `if (!n) return ''`, so the row read `Adjusted:` and then a blank.
      // The backend now answers null instead of 0 (reilm-be utils/arvFetch.js compAdjustments), and
      // both shapes land here.
      //
      // `> 0` rather than a null check, for the two analyses already saved with the zero: they carry
      // it until the next search, and a zero is not a price a house sold for. A negative is the same
      // statement, so one comparison covers both.
      renderCell: ({ row }) => (
        <div className="flex flex-col items-end justify-center gap-y-[2px] py-[4px]">
          {/* The top line gets the dash too, and that is the half the first pass missed.
              compDisplayPrice reads sold_price for a Sold comp, so the very comp Микола asked about
              — Sold, no sale price from the provider — left BOTH lines blank: an empty cell over
              `Adjusted:` and nothing. Falling back to the list price here was the other option and
              is worse: it would print a number the house did not sell for, in the column the ARV is
              built from. */}
          {/* ARV 7.2: 14/600 on the unadjusted price, Frame 469/Frame 189_2. */}
          <span className="text-[14px] font-semibold text-brand-dark leading-tight">
            {compDisplayPrice(row) > 0
              ? formatPrice(compDisplayPrice(row))
              : <span className="font-normal text-brand-placeholder">—</span>}
          </span>
          <span className="text-[13px] text-brand-secondary leading-tight">
            Adjusted:{' '}
            {row.adjustments > 0
              ? formatPrice(row.adjustments)
              : <span className="text-brand-placeholder">—</span>}
          </span>
        </div>
      ),
    },

    // ── Actions ──────────────────────────────────────────────────────────────
    {
      field: '__actions',
      headerName: '',
      width: 90,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      disableColumnMenu: true,
      renderHeader: () => null,
      renderCell: ({ row }) => (
        // The clickable area grows via padding on the button; the icons stay drawn at their own
        // size — a bigger hit target, not a visual resize. `p-[1px]` on both buttons takes Gallery's
        // 16x16 SVG to an 18x18 click box (+12.5% a side) and Exclude/Restore's 12x12 to 14x14
        // (+16.7% a side). No collision risk: `gap-x-[14px]` on the row below is a flex `gap`, which
        // reserves space between the buttons' own border boxes — padding added inside a button's box
        // can never eat into a neighbour's, only into empty space the button itself did not
        // previously claim.
        <div className="flex items-center justify-center gap-x-[14px] w-full">
          <button
            onClick={(e) => { e.stopPropagation(); onGalleryOpen?.(row.photos ?? [], row.propertyId) }}
            className="flex items-center p-[1px] text-[#4F5F4A] hover:text-[#3AA015] transition-colors"
          >
            <ArvGalleryIcon />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onToggleArchived?.(row) }}
            className={`flex items-center p-[1px] text-[#4F5F4A] transition-colors ${row.archived ? 'hover:text-[#4CBC18]' : 'hover:text-[#D85263]'}`}
          >
            {row.archived ? <RestoreIcon /> : <ExcludeIcon />}
          </button>
        </div>
      ),
    },
  ]
}

export function getVerifyColumns() {
  return [
    // ── Address ──────────────────────────────────────────────────────────────
    {
      field: 'address',
      headerName: 'Address',
      width: 300,
      sortable: false,
      disableColumnMenu: true,
      renderHeader: () => <span style={headerCls}>Address</span>,
      renderCell: ({ row }) => {
        const { street, city, state, zip } = row.address ?? {}
        const addr = [street, city, state, zip].filter(Boolean).join(', ')
        return (
          <span style={{ fontSize: 14, fontWeight: 500, color: '#17270D' }}>{addr}</span>
        )
      },
    },

    // ── Status ───────────────────────────────────────────────────────────────
    {
      field: 'status',
      headerName: 'Status',
      width: 75,
      sortable: false,
      disableColumnMenu: true,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => <span style={headerCls}>Status</span>,
      renderCell: ({ value }) => (
        <span
          className="flex h-[18px] px-[6px] py-[2px] rounded"
          style={{
            background: value === 'Sold' ? '#4CBC18' : value === 'Active' ? '#C13A4A' : '#F5C312',
          }}
        >
          <span className="text-[11px] flex text-white items-center">{value?.[0]}</span>
        </span>
      ),
    },

    // ── Distance ─────────────────────────────────────────────────────────────
    {
      field: 'distanceSubj',
      headerName: 'Distance',
      width: 100,
      sortable: false,
      disableColumnMenu: true,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => <span style={headerCls}>Distance</span>,
      renderCell: ({ value }) => (
        <span style={{ fontSize: 14, fontWeight: 500, color: '#17270D' }}>
          {value != null ? `${value.toFixed(2)} mi` : ''}
        </span>
      ),
    },

    // ── Score ────────────────────────────────────────────────────────────────
    {
      field: 'score',
      headerName: 'Score',
      width: 70,
      sortable: false,
      disableColumnMenu: true,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => <span style={headerCls}>Score</span>,
      renderCell: ({ value }) => (
        <span
          className="font-bold text-[14px]"
          style={{
            color: value > 69 ? '#43B017' : value > 59 ? '#7EBA56' : '#17270D',
          }}
        >
          {value ?? ''}
        </span>
      ),
    },

    // ── Date ─────────────────────────────────────────────────────────────────
    {
      field: '__date',
      headerName: 'Date',
      width: 110,
      sortable: false,
      disableColumnMenu: true,
      renderHeader: () => <span style={headerCls}>Date</span>,
      renderCell: ({ row }) => {
        const dateStr = row.status === 'Sold' ? row.last_sold_date : row.list_date
        return (
          <span style={{ fontSize: 14, fontWeight: 400, color: '#17270D' }}>
            {formatDate(dateStr)}
          </span>
        )
      },
    },
    {
      field: 'linkk',
      headerName: '',
      width: 250,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
      <span
      >
        {/* rel: target="_blank" without it lets the opened page read window.opener and navigate it
            (reverse tabnabbing) — this link points at an external listing URL. Both keywords, to
            match components/LegalLink.jsx, the only other target="_blank" in the codebase. */}
        <a target='_blank' rel="noopener noreferrer" href={`${params.value}`}>
          {params.value}
        </a>
      </span>
    ),
    },
    {
      field: 'price',
      headerName: 'price',
      width: 125,
      sortable: false,
      disableColumnMenu: true,
      
    },
    {
      field: 'sold_price',
      headerName: 'sol.pr',
      width: 125,
      sortable: false,
      disableColumnMenu: true,
      
    }
  ]
}
